/**
 * <ChoiceChip> — une option courte dans une rangée (atom).
 *
 * Entre `<Badge>`, qui ne se presse pas, et `<OptionCard>`, qui prend toute la largeur : le chip
 * qu'on coche. Une classe, un type d'activité, une durée, une compétence — des réponses d'un mot,
 * qu'on lit d'un coup d'œil et qu'on aligne. Le chrome d'une `<HorizontalSelectInput>`, qui ne
 * possède justement pas la sélection.
 *
 * Contrôlé : `selected` arrive, `onPress` repart. Sélectionné, il se remplit de l'aplat CHOISI de
 * l'univers (`role-selected`) et porte la coche — la même lecture que la puce du legacy. Cet aplat
 * vaut la couleur de l'univers partout, sauf chez l'élève, où c'est l'orange de son bouton : le
 * blanc ne tient que 3,1:1 sur son orange d'affichage.
 *
 * **`variant="pill"` — la pilule de sélection.** Une rangée de réponses qu'on TAPE plutôt qu'on ne
 * lit (combien d'heures, pour quand, quel créneau, quel motif, combien de retard) prend la forme
 * du bouton-poussoir (`<PushButton>`, taille `sm`), sans en être un : **une sélection n'est pas un
 * bouton d'action.** Ces rangées exprimaient « choisi » par `<Button variant="solid">` et « non
 * choisi » par `outline` ; depuis que la V16 blanchit le secondaire sur la nuit, les deux y étaient
 * identiques. L'état se lit ici à la face :
 *  - non choisie — la surface de la carte (`surface-raised`), le contour du livre (`border`, qui
 *    fait aussi le relief), le libellé à la couleur de l'univers ;
 *  - choisie — l'aplat choisi de l'univers (`role-selected`), son encre et son relief, lus sur le
 *    descripteur.
 * En clair, c'est au pixel près le `solid` / `outline` d'avant ; sur la nuit, blanc contre nuit.
 *
 * **`leading` — un visage devant le libellé** (chip seulement) : la capsule d'un tuteur, la pastille
 * d'un groupe, à `CHOICE_CHIP_LEADING_SIZE`. Il prend la place de la coche — l'aplat dit déjà le
 * choix — et la chip resserre son bord gauche pour que le visage épouse son arrondi, sans changer
 * de hauteur. Le fil du chat du live en a une par tuteur (`<LiveChatChannels>`).
 */
import React from "react"
import { Pressable, type Insets } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { inkOnFill, reliefOnFill, textInkOnFill } from "../../theme/tokens/colors"
import { spacing } from "../../theme/tokens/spacing"
import { PushButton, type PushFace } from "../Buttons/Button"
import { FocusRing, focusRingHost } from "../FocusRing"
import { textLineBox } from "../Skeleton/Skeleton"
import { Row } from "../containers/Row"
import { Icon, type IconName } from "../icons/Icon"
import { Text } from "../typography/Text"

/** `<Pressable>` ne type que `pressed` ; react-native-web ajoute `focused` à l'exécution. */
type PressableVisualState = { pressed: boolean; focused?: boolean }

export type ChoiceChipVariant = "chip" | "pill"

export type ChoiceChipProps = {
    label: string
    selected: boolean
    onPress: () => void
    /** `chip` (défaut) : la chip pleine du livre · `pill` : la pilule de sélection, à la forme du poussoir. */
    variant?: ChoiceChipVariant
    disabled?: boolean
    /** Largeur maximale, pour un libellé long (une compétence). Sans borne par défaut. Chip seulement. */
    maxWidth?: number
    /** La pilule remplit sa colonne — la grille de créneaux au large. Pilule seulement. */
    fullWidth?: boolean
    /**
     * Une icône devant le libellé — un filtre qu'on reconnaît d'un coup d'œil. Sur la chip, elle prend la
     * place de la coche, comme `leading`. Absente (défaut) : rien, la coche quand elle est choisie.
     */
    iconName?: IconName
    /**
     * Un visage devant le libellé, à `CHOICE_CHIP_LEADING_SIZE` : il remplace la coche. Chip
     * seulement. Absent (défaut) : la coche, comme avant.
     */
    leading?: React.ReactNode
    /**
     * La chip lance un geste (ouvrir une vue, régler un champ) au lieu de répondre à une question : le
     * lecteur d'écran l'annonce comme un bouton, et `selected` n'est plus que son aspect. Défaut `false` : une
     * option, annoncée comme un bouton radio.
     */
    action?: boolean
    /** Une chip `action` qui ouvre une vue : la vue est ouverte (`aria-expanded`). Absent : rien d'annoncé. */
    expanded?: boolean
    /** Ce que dit le lecteur d'écran, quand la chip porte plus que son libellé (un compte). Défaut : le libellé. */
    accessibilityLabel?: string
    /**
     * Agrandit la zone de toucher au-delà de la chip (chip seulement), sans changer sa forme : une
     * rangée de chips seules, où rien d'autre n'est à portée du doigt. Un nombre l'étend des quatre côtés ;
     * des côtés (`{ top, bottom }`) seulement là où il manque de la place, sans déborder sur la voisine.
     * Défaut : aucune.
     */
    hitSlop?: number | Insets
    testID?: string
}

/** L'aplat d'une option choisie. L'univers, pas `primary` : c'est une sélection, pas une action. */
const CHOSEN = "role-selected"

/** Les deux faces de la pilule. */
const PILL_FACE: Record<"chosen" | "unchosen", PushFace> = {
    chosen: { fill: CHOSEN, border: null, relief: reliefOnFill(CHOSEN), ink: inkOnFill(CHOSEN), hoverVeil: true },
    unchosen: { fill: "surface-raised", border: "border", relief: "border", ink: textInkOnFill(CHOSEN) },
}

/** L'encre de la chip pleine choisie : celle de l'aplat choisi, comme la pilule. */
const CHOSEN_INK = inkOnFill(CHOSEN)

export function ChoiceChip(props: ChoiceChipProps) {
    const {
        label,
        selected,
        onPress,
        variant = "chip",
        disabled = false,
        maxWidth,
        fullWidth = false,
        iconName,
        leading,
        action = false,
        expanded,
        accessibilityLabel,
        hitSlop,
        testID,
    } = props

    const { theme } = useUnistyles()

    if (variant === "pill") {
        return (
            <PushButton
                testID={testID}
                label={label}
                size="sm"
                face={selected ? PILL_FACE.chosen : PILL_FACE.unchosen}
                accessibilityRole={action ? "button" : "radio"}
                selected={action ? undefined : selected}
                onPress={onPress}
                disabled={disabled}
                fullWidth={fullWidth}
                iconName={iconName}
            />
        )
    }

    return (
        <Pressable
            testID={testID}
            onPress={onPress}
            disabled={disabled}
            accessibilityRole={action ? "button" : "radio"}
            accessibilityLabel={accessibilityLabel ?? label}
            accessibilityState={action ? { disabled } : { selected, disabled }}
            // `aria-expanded` et non `accessibilityState` : react-native-web 0.20 ignore ce dernier.
            aria-expanded={action ? expanded : undefined}
            hitSlop={hitSlop}
            style={({ pressed }: PressableVisualState) => [
                focusRingHost,
                styles.chip(selected, disabled, maxWidth, leading != null),
                pressed && !disabled ? styles.pressed : undefined,
            ]}
        >
            {({ focused }: PressableVisualState) => (
                <>
                    <FocusRing visible={focused === true && !disabled} radius={theme.radius.full} />
                    <Row gap="2" align="center">
                        {leading}
                        {leading == null && (iconName != null || selected) && (
                            <Icon
                                name={iconName ?? "check"}
                                size={14}
                                color={
                                    iconName == null || selected ? CHOSEN_INK : disabled ? "text-muted" : "text-primary"
                                }
                            />
                        )}
                        <Text
                            size={CHIP_TEXT}
                            weight={selected ? "bold" : "regular"}
                            color={disabled ? "text-muted" : selected ? CHOSEN_INK : "text-primary"}
                            numberOfLines={2}
                            style={styles.label}
                        >
                            {label}
                        </Text>
                    </Row>
                </>
            )}
        </Pressable>
    )
}

/** Le retrait vertical et la taille du libellé de la chip pleine : sa feuille et sa hauteur les lisent. */
const CHIP_PADDING_V = "2"
const CHIP_TEXT = "text-sm"

/**
 * La hauteur de la chip pleine (`variant="chip"`) d'une ligne : ses deux retraits et une ligne de
 * son libellé — la place que son squelette lui garde. La pilule, elle, est une face de poussoir :
 * `pushButtonBoxHeight("sm")`.
 */
export const CHOICE_CHIP_HEIGHT = 2 * spacing[CHIP_PADDING_V] + textLineBox(CHIP_TEXT)

/** Le retrait gauche d'une chip qui porte un visage : le même de tous côtés autour de lui. */
const LEADING_INSET = "1"

/** Le diamètre d'un visage dans la chip (`leading`) : la hauteur de la chip, moins son retrait. */
export const CHOICE_CHIP_LEADING_SIZE = CHOICE_CHIP_HEIGHT - 2 * spacing[LEADING_INSET]

const styles = StyleSheet.create((theme) => ({
    /** Pleine, sans contour ni relief : la chip du livre V13 se lit à son aplat. */
    chip: (selected: boolean, disabled: boolean, maxWidth: number | undefined, withLeading: boolean) => ({
        paddingVertical: theme.spacing[CHIP_PADDING_V],
        paddingLeft: theme.spacing[withLeading ? LEADING_INSET : "3"],
        paddingRight: theme.spacing["3"],
        borderRadius: theme.radius.full,
        backgroundColor: selected ? theme.colors[CHOSEN] : theme.colors["surface-subtle"],
        opacity: disabled ? 0.5 : 1,
        maxWidth,
    }),
    pressed: { opacity: 0.8 },
    label: { flexShrink: 1 },
}))
